import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { company: "", contactName: "", contactEmail: "", contactPhone: "", source: "manual", interest: "" };
// Item 3 (Aug 2026 Sales dashboard scope pass) — Instagram/Facebook/Other
// added alongside the existing channels (kept for backward compatibility
// with leads already recorded under them — `source` is stored as free
// text server-side, never a fixed enum, so no backend change is needed).

export function NewLeadDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const valid = form.company.trim() && (form.contactEmail.trim() || form.contactPhone.trim());

  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim()]));
      const { data } = await api.post("/leads", payload);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["commercial-list"] }),
        qc.invalidateQueries({ queryKey: ["leads"] }),
        qc.invalidateQueries({ queryKey: ["actions"] }),
      ]);
      setForm(EMPTY);
      onOpenChange(false);
      toast.success(`Lead ${data.id} created`);
      navigate(`/sales/leads/${data.id}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || error.response?.data?.message || "Lead could not be created");
    } finally { setBusy(false); }
  };

  return <Dialog open={open} onOpenChange={(value) => !busy && onOpenChange(value)}>
    <DialogContent className="sm:max-w-xl">
      <DialogHeader><DialogTitle>Create new lead</DialogTitle><DialogDescription>Capture the first customer contact. Qualification and shipment requirements follow in the lead workspace.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-4" data-testid="new-lead-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label htmlFor="lead-company">Company *</Label><Input id="lead-company" value={form.company} onChange={set("company")} className="mt-1.5" autoFocus required data-testid="new-lead-company" /></div>
          <div><Label htmlFor="lead-contact">Contact name</Label><Input id="lead-contact" value={form.contactName} onChange={set("contactName")} className="mt-1.5" data-testid="new-lead-contact" /></div>
          <div><Label htmlFor="lead-source">Lead source</Label><select id="lead-source" value={form.source} onChange={set("source")} className="mt-1.5 flex h-9 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="new-lead-source"><option value="manual">Manual entry</option><option value="whatsapp">WhatsApp</option><option value="website">Website</option><option value="instagram">Instagram</option><option value="facebook">Facebook</option><option value="email">Email</option><option value="phone">Phone</option><option value="referral">Referral</option><option value="social">Social media (other)</option><option value="other">Other</option></select></div>
          <div><Label htmlFor="lead-email">Email</Label><Input id="lead-email" type="email" value={form.contactEmail} onChange={set("contactEmail")} className="mt-1.5" data-testid="new-lead-email" /></div>
          <div><Label htmlFor="lead-phone">Phone number *</Label><Input id="lead-phone" type="tel" value={form.contactPhone} onChange={set("contactPhone")} className="mt-1.5" data-testid="new-lead-phone" /></div>
          <div className="sm:col-span-2"><Label htmlFor="lead-interest">Customer's message / inquiry</Label><Textarea id="lead-interest" value={form.interest} onChange={set("interest")} placeholder="What the customer said/asked for, e.g. Air import from Shanghai to Dubai" rows={3} className="mt-1.5" data-testid="new-lead-interest" /></div>
        </div>
        <p className="text-xs text-slate-500">Provide at least an email address or phone number so the sales team can contact the lead.</p>
        <DialogFooter><Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button><Button type="submit" disabled={!valid || busy} data-testid="new-lead-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create lead</Button></DialogFooter>
      </form>
    </DialogContent>
  </Dialog>;
}
